import { ChevronDown, Download, Filter, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type DrillThroughExportOption<T extends string> = {
  value: T;
  label: string;
};

type DrillThroughExportControlProps<T extends string> = {
  options: readonly DrillThroughExportOption<T>[];
  selectedOptions: readonly T[];
  onToggleOption: (option: T) => void;
  onDownload: () => void;
  isExporting: boolean;
  disabled?: boolean;
};

export function DrillThroughExportControl<T extends string>({
  options,
  selectedOptions,
  onToggleOption,
  onDownload,
  isExporting,
  disabled = false,
}: DrillThroughExportControlProps<T>) {
  const cannotDownload = disabled || isExporting || selectedOptions.length === 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2 border-border/80 bg-background/70 px-3 shadow-sm hover:bg-accent">
            <Filter className="h-4 w-4" />
            Export Filter
            <ChevronDown className="h-4 w-4 opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-2 shadow-lg">
          <p className="px-2 pb-2 pt-1 text-sm text-muted-foreground">Choose export options</p>
          <div className="space-y-1">
            {options.map((option) => {
              const id = `export-option-${option.value}`;
              return (
                <Label
  key={option.value}
  htmlFor={id}
  className="
    flex cursor-pointer items-center gap-3 rounded-md
    px-2 py-2.5 text-base font-medium transition-colors
    hover:bg-accent hover:text-white
    dark:hover:text-foreground
  "
>
                  <Checkbox
                    id={id}
                    checked={selectedOptions.includes(option.value)}
                    onCheckedChange={() => onToggleOption(option.value)}
                  />
                  {option.label}
                </Label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <Button size="sm" className="gap-2 shadow-sm" onClick={onDownload} disabled={cannotDownload}>
        {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {isExporting ? "Preparing export…" : "Export to Excel"}
      </Button>
    </div>
  );
}
